import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import endpoints from '../../config/endpoints';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import { DATE_ALL, DateRange, DateRangePresets, toToman } from './AnalyticsLayout';
import { Sidebar } from '../Layout/Sidebar';

const COLORS = ['#1A6BFF', '#4D8FFF', '#7A8CA0', '#3D4F6B', '#0F1923', '#1254D6', '#93C5FD', '#64748B'];

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch();

  const passedRange: DateRange | undefined = (location.state as any)?.dateRange;
  const [dateRange, setDateRange] = useState<DateRange>(passedRange || { from: DATE_ALL, to: DATE_ALL });
  const [data, setData] = useState<any>(null);

  const fetchData = useCallback(async () => {
    if (!id) return;
    dispatch(setLoading(true));
    const apiQuery = dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : {};
    const res = await restApi(endpoints.analytics.productDetail + id).get(apiQuery);
    if (res.code === 200) setData(res.data);
    dispatch(setLoading(false));
  }, [id, dateRange, dispatch]);

  // Presets apply immediately (no separate "apply" button) — refetch
  // whenever the resolved date range changes.
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportBuyers = () => {
    if (!data?.topBuyers) return;
    const rows = data.topBuyers.map((b: any, i: number) => ({
      '#': i + 1,
      'نام': b.name || '—',
      'موبایل': b.phoneNumber,
      'شرکت': b.companyName || '—',
      'تعداد خریداری‌شده': b.count,
      'مجموع پرداختی (تومان)': toToman(b.spend),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'خریداران');
    XLSX.writeFile(wb, `product-${id}-buyers.xlsx`);
  };

  return (
    <body className="dashboardBody">
      <Sidebar />
      <main className="analyticsMain">

        {/* Back + filter bar */}
        <div style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Link to="/analytics/products" className="analyticsBackBtn">← بازگشت به محصولات</Link>
          <DateRangePresets dateRange={dateRange} onChange={setDateRange} />
        </div>

        {/* Product info card */}
        {data?.product && (
          <div style={{
            background: 'linear-gradient(135deg, #14337D 0%, #1e4db7 100%)',
            borderRadius: 14, padding: 24, color: '#fff', marginBottom: 20,
            display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: 20,
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0,
            }}>🏷️</div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{data.product.title}</div>
              <div style={{ fontSize: 13, opacity: 0.85, display: 'flex', flexDirection: 'row-reverse', gap: 20, flexWrap: 'wrap' }}>
                {data.product.sku && <span>SKU: {data.product.sku}</span>}
                {data.product.price && <span>قیمت پایه: {tools.formatPrice(toToman(data.product.price))} تومان</span>}
              </div>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="analyticsKpiRow">
          <div className="analyticsKpiCard">
            <div className="analyticsKpiIcon kpiBlue">📦</div>
            <div className="analyticsKpiValue">{data?.totalCount ?? 0}</div>
            <div className="analyticsKpiLabel">تعداد واحد فروخته‌شده</div>
          </div>
          <div className="analyticsKpiCard">
            <div className="analyticsKpiIcon kpiGreen">💰</div>
            <div className="analyticsKpiValue">{tools.formatPrice(toToman(data?.totalRevenue))} تومان</div>
            <div className="analyticsKpiLabel">مجموع درآمد از این محصول</div>
          </div>
        </div>

        {/* Sales by month */}
        {(data?.salesByMonth?.length ?? 0) > 0 && (
          <div className="analyticsChartCard" style={{ marginBottom: 20 }}>
            <div className="analyticsChartTitle">فروش ماهانه (عدد و تومان)</div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.salesByMonth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fontFamily: 'Yekan Bakh' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => tools.formatPrice(toToman(v))} width={90} />
                <Tooltip
                  formatter={(v: any, name: any) =>
                    name === 'count' ? [v, 'عدد'] : [tools.formatPrice(toToman(v)) + ' تومان', 'درآمد']
                  }
                  labelStyle={{ textAlign: 'right' }}
                />
                <Line yAxisId="left" type="monotone" dataKey="count" stroke="#1A6BFF" strokeWidth={2} dot={{ r: 5 }} name="count" />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#0F1923" strokeWidth={2} dot={{ r: 5 }} name="revenue" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top buyers chart */}
        {(data?.topBuyers?.length ?? 0) > 0 && (
          <>
            <div className="analyticsChartCard" style={{ marginBottom: 20 }}>
              <div className="analyticsChartTitle">پرخریدترین مشتریان این محصول (عدد)</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={data.topBuyers.slice(0, 8).map((b: any) => ({ name: b.name || b.phoneNumber, count: b.count }))}
                  layout="vertical" margin={{ right: 16, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fontFamily: 'Yekan Bakh' }} />
                  <Tooltip formatter={(v: any) => [v, 'عدد']} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {data.topBuyers.slice(0, 8).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Buyers table */}
            <div className="analyticsTableCard">
              <div className="analyticsTableHeader">
                <div className="analyticsChartTitle" style={{ margin: 0 }}>جدول خریداران</div>
                <button className="analyticsExportBtn" onClick={exportBuyers}>⬇ خروجی اکسل</button>
              </div>
              <table className="alternating-table" style={{ width: '100%' }}>
                <thead>
                  <tr><th>#</th><th>نام</th><th>موبایل</th><th>شرکت</th><th>تعداد خرید (عدد)</th><th>مجموع پرداختی (تومان)</th></tr>
                </thead>
                <tbody>
                  {data.topBuyers.map((b: any, i: number) => (
                    <tr key={b.userId} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                      <td>{i + 1}</td>
                      <td>{b.name || '—'}</td>
                      <td style={{ direction: 'ltr' }}>{b.phoneNumber}</td>
                      <td>{b.companyName || '—'}</td>
                      <td><strong>{b.count}</strong></td>
                      <td>{tools.formatPrice(toToman(b.spend))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {data && !data.totalCount && (
          <div className="analyticsEmptyState">
            <div style={{ fontSize: 48 }}>📦</div>
            <p>در این بازه زمانی فروشی برای این محصول ثبت نشده است</p>
          </div>
        )}

      </main>
    </body>
  );
}
